import { Suspense, lazy } from "react";
import { Route, Navigate } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { api } from "./utils/api";
import { permissionService } from "./services/permissionService";
import { renderExecutiveAnalyticsRoutes } from "../admin-shared/analytics/renderAnalyticsRoutes";

const LoginSign = lazy(() => import("./pages/LoginSign"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));
const HqAdmins = lazy(() => import("./pages/HqAdmins"));
const Regions = lazy(() => import("./pages/Regions"));
const CompanyProfile = lazy(() => import("./pages/CompanyProfile"));
const NaturalsKpis = lazy(() => import("./pages/EnterpriseKpis"));
const Complaints = lazy(() => import("./pages/Complaints"));
const HqCustomersPage = lazy(() => import("../admin-shared/HqCustomersPage"));
const HqCustomerDetailPage = lazy(() => import("../admin-shared/HqCustomerDetailPage"));
const HqCheckinAnalyticsPage = lazy(() => import("../admin-shared/HqCheckinAnalyticsPage"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const NaturalsOverviewPage = lazy(() => import("../admin-shared/pages/EnterpriseOverviewPage"));
const LicenseManagementPage = lazy(() => import("../admin-shared/LicenseManagementPage"));

const RouteFallback = () => <Loader fullScreen />;
const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const PermRoute = ({ children, check }) => {
  if (typeof check === "function" && !check()) {
    return <Navigate to="/naturals/executive/overview" replace />;
  }
  return children;
};

export function renderExecutiveRoutes({ executiveHome = false } = {}) {
  return (
    <>
      {executiveHome ? <Route path="/" element={<Navigate to="/naturals/executive" replace />} /> : null}
      <Route path="/naturals/executive" element={<Navigate to="/naturals/login" replace />} />
      <Route
        path="/naturals/executive/overview"
        element={
          <Layout>
            {withSuspense(<NaturalsOverviewPage portal="executive" permissionService={permissionService} />)}
          </Layout>
        }
      />
      <Route path="/naturals/executive/nyafaz-hub" element={<Navigate to="/naturals/executive/overview" replace />} />
      <Route
        path="/naturals/executive/super-admin-dashboard"
        element={<Layout>{withSuspense(<SuperAdminDashboard />)}</Layout>}
      />
      <Route
        path="/naturals/executive/licenses"
        element={
          <Layout>
            {withSuspense(
              <LicenseManagementPage
                api={api}
                portalBase="/naturals/executive"
                portalLabel="Executive"
              />,
            )}
          </Layout>
        }
      />
      <Route path="/naturals/executive/hq-admins" element={<Layout>{withSuspense(<HqAdmins />)}</Layout>} />
      <Route path="/naturals/executive/regions" element={<Layout>{withSuspense(<Regions />)}</Layout>} />
      <Route
        path="/naturals/executive/company-profile"
        element={<Layout>{withSuspense(<CompanyProfile />)}</Layout>}
      />
      <Route
        path="/naturals/executive/naturals-kpis"
        element={<Layout>{withSuspense(<NaturalsKpis />)}</Layout>}
      />
      <Route
        path="/naturals/executive/store-location-requests"
        element={<Navigate to="/naturals/executive/super-admin-dashboard" replace />}
      />
      <Route path="/naturals/executive/complaints" element={<Layout>{withSuspense(<Complaints />)}</Layout>} />
      <Route
        path="/naturals/executive/customers"
        element={
          <Layout>
            {withSuspense(<HqCustomersPage api={api} portalBase="/naturals/executive" portalLabel="Executive" />)}
          </Layout>
        }
      />
      <Route
        path="/naturals/executive/customers/:faceUserId"
        element={
          <Layout>{withSuspense(<HqCustomerDetailPage api={api} portalBase="/naturals/executive" />)}</Layout>
        }
      />
      <Route
        path="/naturals/executive/check-in-analytics"
        element={
          <Layout>
            {withSuspense(
              <HqCheckinAnalyticsPage
                api={api}
                portalBase="/naturals/executive"
                portalLabel="Executive"
                Loader={Loader}
              />,
            )}
          </Layout>
        }
      />
      <Route path="/naturals/executive/edit-profile" element={<Layout>{withSuspense(<EditProfile />)}</Layout>} />
      {renderExecutiveAnalyticsRoutes({
        withSuspense,
        Layout,
        PermRoute,
        canViewSkin: () => permissionService.canAccessSkinAnalytics(),
        canViewHair: () => permissionService.canAccessHairAnalytics(),
        canViewFlow: () => permissionService.canAccessFlowAnalytics(),
        canViewGuestOps: () => permissionService.canAccessGuestOpsAnalytics(),
        hqApi: api,
      })}
    </>
  );
}
